import { TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Box, Typography, Link, ListSubheader, Tooltip, Button } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SchemaItem, StructureColumnTypeEnum, StructureItemType } from '../@shared/Models/Models';
import { ModalBase } from '../components/ModalBase';
import { changeAIModelTrainerTypeId, closeTrainedModelModalAction, saveTrainedModelModalAction } from '../sagas/trainedModelSaga/trainedModelSagaActions';
import { getTrainedModelName, getAIModelTrainerTypeId, setTrainedModelName, getIsShowTrainedModelModal, setTrainedModelSettings, getTrainedModelSettings, setTrainedModelDescription, getTrainedModelDescription, getAIModelTrainedModelId, getIsModelUpdating, getModelDependencies, setModelDependency } from '../reducers/trainedModel/trainedModelReducer';
import { CodeMirrorStyles, CodeMirrorTheme, FlexBoxInline, InputBaseSx, LinkSxSmall, SelectMenuItemSx, SelectMenuProps, SelectMenuSubTitleSx, TooltipSlotProps } from '../@shared/templates';
import { json } from '@codemirror/lang-json';
import CodeMirror, { ViewUpdate } from '@uiw/react-codemirror';
import { Palette, Sizes } from '../@shared/palette';
import { ContextHelper } from '../components/ContextHelper';
import { getOpenedSchema } from '../reducers/data/dataReducer';
import { getDefaultModelSettings } from '../@shared/modelsSettingsBase';
import { ModelType } from '../@shared/Models/ModelTypes';
import { getGroupedModelTypes } from '../@shared/Models/ModelGroupType';
import { ModelDependencySelect } from '../components/ModelDependencySelect';
import { getGuideStep, setGuideStep } from '../reducers/main/mainReducer';
import { GuideStepEnum } from '../reducers/main/mainModel';
import GuideCallout from '../components/GuideCallout';


export const withAddOrUpdateTrainedModel = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const dispatch = useDispatch();
  const handleClose = () => dispatch(closeTrainedModelModalAction())
  const onSave = () => {
    dispatch(saveTrainedModelModalAction());
  }
  const nameValue = useSelector(getTrainedModelName);
  const aiModelTrainerTypeId = useSelector(getAIModelTrainerTypeId);
  const trainedModelSettings = useSelector(getTrainedModelSettings);
  const description = useSelector(getTrainedModelDescription);
  const schema = useSelector(getOpenedSchema);
  const isOpen = useSelector(getIsShowTrainedModelModal);
  const modelId = useSelector(getAIModelTrainedModelId);
  const isModelUpdating = useSelector(getIsModelUpdating);
  const modelDependencies = useSelector(getModelDependencies);
  const guideStep = useSelector(getGuideStep);

  React.useEffect(() => {
    if (!modelId && aiModelTrainerTypeId && schema) {
      dispatch(setTrainedModelSettings(getDefaultModelSettings(aiModelTrainerTypeId, schema.schemaItems)));
    }
  }, [dispatch, schema, aiModelTrainerTypeId, modelId])

  React.useEffect(() => {
    if (!aiModelTrainerTypeId && schema) {
      const firstTrainer = Object.keys(ModelType).find(x => isSchemaValidForTrainer(schema.schemaItems, x as ModelType)) as ModelType ?? ModelType.AveragedPerceptron;
      dispatch(changeAIModelTrainerTypeId(firstTrainer))
    }
  }, [dispatch, schema, isOpen, aiModelTrainerTypeId, modelId])

  const getContextHelper = React.useCallback(
    (type?: ModelType) => {
      if (!type) {
        return;
      }

      switch (type) {
        case ModelType.AveragedPerceptron:
          return <>A linear classification algorithm that averages multiple perceptron models to improve generalization.</>;
        case ModelType.SdcaNonCalibrated:
          return <>Stochastic Dual Coordinate Ascent (SDCA) for linear classification without probability calibration.</>;
        case ModelType.SgdNonCalibrated:
          return <>Stochastic Gradient Descent (SGD) for linear classification without probability calibration.</>;
        case ModelType.LinearSvm:
          return <>Linear Support Vector Machine for binary classification problems.</>;
        case ModelType.LbfgsLogisticRegression:
          return <>Logistic regression using the L-BFGS optimization algorithm for binary classification.</>;
        case ModelType.KMeans:
          return <>K-Means clustering algorithm for unsupervised grouping of data points.</>;
        case ModelType.NaiveBayes:
          return <>A probabilistic classifier based on Bayes’ theorem with strong independence assumptions.</>;
        case ModelType.SdcaRegression:
          return <>Stochastic Dual Coordinate Ascent (SDCA) for linear regression tasks.</>;
        case ModelType.SdcaMaximumEntropy:
          return <>SDCA for multi-class classification using the maximum entropy principle.</>;
        case ModelType.LbfgsPoissonRegression:
          return <>Poisson regression with L-BFGS optimization, useful for count data.</>;
        case ModelType.OnlineGradientDescent:
          return <>Gradient Descent algorithm that updates the model incrementally as new data arrives.</>;
        case ModelType.LbfgsMaximumEntropy:
          return <>Multi-class maximum entropy classification using the L-BFGS optimizer.</>;
        case ModelType.OneVersusAll:
          return <>Transforms a binary classifier into a multi-class classifier by training one model per class.</>;
        case ModelType.PairwiseCoupling:
          return <>Combines multiple binary classifiers to perform multi-class classification by comparing class pairs.</>;
        case ModelType.NeuralNetwork:
          return <>A multi-layer feedforward neural network for classification or regression tasks.</>;
        case ModelType.Onnx:
          return <>Use a pre-trained ONNX model for inference or fine-tuning within the platform.</>;
        case ModelType.LightGbm:
          return <>Light Gradient Boosting Machine (LightGBM) for fast, distributed, high-performance gradient boosting classification.</>;
        case ModelType.RandomForest:
          return <>An ensemble learning method using multiple decision trees for classification tasks.</>;
        case ModelType.LightGbmRegression:
          return <>Light Gradient Boosting Machine (LightGBM) optimized for regression tasks.</>;
        case ModelType.RandomForestRegression:
          return <>An ensemble learning method using multiple decision trees for regression tasks.</>;
        default:
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const _exhaustive: never = type;
          throw new Error(`Unhandled color: ${type}`);
      }
    },
    []
  );

  const getDocsLink = React.useCallback((type?: ModelType) => {
    if (!type) {
      return;
    }
    switch (type) {
      case ModelType.AveragedPerceptron:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#averaged-perceptron">Averaged Perceptron</Link>;
      case ModelType.SdcaNonCalibrated:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#sdca-non-calibrated">SDCA Non Calibrated</Link>;
      case ModelType.SgdNonCalibrated:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#sgd-non-calibrated">SGD Non Calibrated</Link>;
      case ModelType.LinearSvm:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#linear-svm">Linear SVM</Link>;
      case ModelType.LbfgsLogisticRegression:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#lbfgs-logistic-regression">LBFGS Logistic Regression</Link>;
      case ModelType.KMeans:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#k-means">K Means</Link>;
      case ModelType.NaiveBayes:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#naive-bayes">Naive Bayes</Link>;
      case ModelType.SdcaRegression:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#sdca-regression">SDCA Regression</Link>;
      case ModelType.SdcaMaximumEntropy:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#sdca-maximum-entropy">SDCA Maximum Entropy</Link>;
      case ModelType.LbfgsPoissonRegression:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#lbfgs-poisson-regression">LBFGS Poisson Regression</Link>;
      case ModelType.OnlineGradientDescent:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#online-gradient-descent">Online Gradient Descent</Link>;
      case ModelType.LbfgsMaximumEntropy:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#lbfgs-maximum-entropy">LBFGS Maximum Entropy</Link>;
      case ModelType.OneVersusAll:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#one-versus-all">One Versus All</Link>;
      case ModelType.PairwiseCoupling:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#pairwise-coupling">Pairwise Coupling</Link>;
      case ModelType.NeuralNetwork:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#neural-network">Neural Network</Link>;
      case ModelType.Onnx:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#onnx">ONNX</Link>;
      case ModelType.LightGbm:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#lightgbm">LightGBM</Link>;
      case ModelType.RandomForest:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#random-forest">Random Forest</Link>;
      case ModelType.LightGbmRegression:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#lightgbm-regression">LightGBM Regression</Link>;
      case ModelType.RandomForestRegression:
        return <Link sx={LinkSxSmall} target="_blank" href="/Documentation#random-forest-regression">Random Forest Regression</Link>;
      default:
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _exhaustive: never = type;
        throw new Error(`Unhandled color: ${type}`);
    }
  }, [])

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setTrainedModelName(e.target.value))
  }

  const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setTrainedModelDescription(e.target.value))
  }

  const onChangeSettings = (value: string, _: ViewUpdate) => {
    dispatch(setTrainedModelSettings(value))
  }

  const onChangeType = (e: SelectChangeEvent<ModelType>) => {
    dispatch(changeAIModelTrainerTypeId(e.target.value as ModelType))
  }

  const fillExample = () => {
    dispatch(setTrainedModelName("Churn_Predictor"));
    dispatch(setTrainedModelDescription("Neural network model to predict customer churn."));
    dispatch(changeAIModelTrainerTypeId(ModelType.NeuralNetwork));
    dispatch(setGuideStep(GuideStepEnum.SaveModel));
  }

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={onSave}
      title="Model"
      isProcessing={isModelUpdating}
      isDisabledSave={isModelUpdating}
      saveBtnTooltip={guideStep === GuideStepEnum.SaveModel ? <GuideCallout>Click to save your model.</GuideCallout> : undefined}
    >
      <Box sx={{ ...FlexBoxInline, marginBottom: "5px" }}>
        <Tooltip placement="left-start" open={guideStep === GuideStepEnum.FillModelModal} slotProps={TooltipSlotProps} title={
          <GuideCallout>
            Provide a name and description for your model. Then select the algorithm you want to use for training. The available algorithms depend on the structure of your dataset.
            <br />
            <Button
              sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }}
              onClick={() => {
                fillExample();
              }}>
              Example
            </Button>
          </GuideCallout>
        } arrow>
          <TextField sx={{ ...InputBaseSx }} size='small' value={nameValue} onChange={onChangeName} fullWidth className='' id="trained-model-name-field" label="Name" variant="outlined" />
        </Tooltip>
        <ContextHelper content={getContextHelper(aiModelTrainerTypeId)}>
          <FormControl size='small' sx={{ ...InputBaseSx, minWidth: "200px" }}>
            <InputLabel id="trained-model-type-label">Type</InputLabel>
            <Select
              labelId="trained-model-type-label"
              id="trained-model-type-input"
              value={aiModelTrainerTypeId}
              label="Type"
              onChange={onChangeType}
              MenuProps={SelectMenuProps}
            >
              {getGroupedModelTypes().flatMap(x => [
                <ListSubheader sx={SelectMenuSubTitleSx} key={x.groupType}>{x.displayName}</ListSubheader>,
                ...x.modelTypes.map(m =>
                  <MenuItem
                    disabled={!isSchemaValidForTrainer(schema?.schemaItems, m.modelType)}
                    key={m.modelType}
                    sx={{ ...SelectMenuItemSx, paddingLeft: "20px" }}
                    value={m.modelType}
                  >
                    {m.label}
                  </MenuItem>)
              ])}
            </Select>
          </FormControl>
        </ContextHelper>

      </Box>
      <Box sx={{ paddingTop: "0px", marginBottom: "10px" }}>
        <TextField sx={InputBaseSx} size='small' value={description} onChange={onChangeDescription} fullWidth className='' id="trained-model-description-field" label="Description" variant="outlined" />
      </Box>
      <ContextHelper boxSx={{ marginBottom: "5px" }} content={<>Specify model training settings in JSON format. The available parameters depend on the selected algorithm. Refer to the {getDocsLink(aiModelTrainerTypeId)} for a full list of supported fields and examples.</>}>
        <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Settings:</Typography>
      </ContextHelper>
      <CodeMirror minHeight='100px' value={trainedModelSettings} maxHeight="400px" extensions={[json()]} onChange={onChangeSettings} style={CodeMirrorStyles} theme={CodeMirrorTheme} />
      {schema && schema.schemaItems.filter(x => x.valueTypeId === StructureItemType.Model).length > 0 &&
        <ContextHelper boxSx={{ marginTop: "20px", marginBottom: "5px" }} content={<>Specify model dependencies for model schema items. It will be used during training, metrics calculation and predictions.</>}>
          <Typography sx={{ fontSize: Sizes.textPrimary, color: Palette.textColorPrimary }}>Model dependencies:</Typography>
        </ContextHelper>}
      {schema?.schemaItems.filter(x => x.valueTypeId === StructureItemType.Model).map((modelItem) =>
        <ModelDependencySelect
          key={modelItem.id}
          modelItem={modelItem}
          value={modelDependencies.find(dep => dep.schemaItemId === modelItem.id)?.refModelId || ''}
          onChange={(schemaItemId, refModelId) => dispatch(setModelDependency({ schemaItemId, refModelId }))}
        />
      )}
    </ModalBase>
    <Component {...props as T} />
  </>
}

function isSchemaValidForTrainer(
  schema: SchemaItem[] | undefined,
  trainer: ModelType
): boolean {
  if (!schema) {
    return true;
  }

  const features = schema.filter((item) => item.typeId === StructureColumnTypeEnum.Feature);
  const targets = schema.filter((item) => item.typeId === StructureColumnTypeEnum.Target);

  switch (trainer) {
    case ModelType.KMeans:
      return features.length > 0 && targets.length === 0;

    case ModelType.AveragedPerceptron:
    case ModelType.SdcaNonCalibrated:
    case ModelType.SgdNonCalibrated:
    case ModelType.LinearSvm:
    case ModelType.LbfgsLogisticRegression:
    case ModelType.RandomForest:
      return (
        features.length > 0 &&
        targets.length === 1 &&
        [StructureItemType.Category].includes(
          targets[0].valueTypeId
        ) &&
        targets[0].categories?.length === 2
      );
    case ModelType.SdcaMaximumEntropy:
    case ModelType.LbfgsMaximumEntropy:
    case ModelType.NaiveBayes:
    case ModelType.OneVersusAll:
    case ModelType.PairwiseCoupling:
    case ModelType.LightGbm:
      return (
        features.length > 0 &&
        targets.length === 1 &&
        [StructureItemType.String, StructureItemType.Category].includes(
          targets[0].valueTypeId
        )
      );
    case ModelType.NeuralNetwork:
      return (
        features.length > 0 &&
        targets.length > 0 &&
        [StructureItemType.Number, StructureItemType.NumberArray, StructureItemType.Category].includes(
          targets[0].valueTypeId
        )
      );
    case ModelType.SdcaRegression:
    case ModelType.LbfgsPoissonRegression:
    case ModelType.OnlineGradientDescent:
    case ModelType.LightGbmRegression:
    case ModelType.RandomForestRegression:
      return (
        features.length > 0 &&
        targets.length === 1 &&
        targets[0].valueTypeId === StructureItemType.Number
      );
    case ModelType.Onnx:
      return true;
    default:
      return false;
  }
}